'use client';

import { useQuery } from '@tanstack/react-query';

import { getHighestPnlPositionApi } from '@flipster/apis/v2/history';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import ShareIcon from '@flipster/ui/assets/svgs/share.svg';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import webviewHandler from '@flipster/utils/webview-handler';

import { LeverageBadge, OrderSideBadge } from '../../../misc/badge';
import { SymbolDisplay } from '../../../misc/display';
import { useAuth } from '../../../shared/auth/state';
import { PnlShareModal } from '../../../shared/pnl-share-modal';

interface Props {
  isAirdrop: boolean;
}

const HighestPnl = ({ isAirdrop }: Props) => {
  const { isCookieLoggedIn } = useAuth();
  const overlay = useOverlay();

  const { data, isPending } = useQuery({
    queryKey: ['history', 'getHighestPnlPositionApi', isAirdrop],
    queryFn: () => getHighestPnlPositionApi({ isAirdrop }),
    select: (res) => res.data.position,
    enabled: isCookieLoggedIn,
  });

  if (isPending || !data) return null;

  const realizedPnl = data.netRealizedPnl;
  const realizedPnlPct = data.netRealizedPnlPct;

  return (
    <section className={css({ pt: { base: 0, tablet: 12 }, tablet: { w: 343 } })} data-test-id='ps_banner_no1'>
      <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Your_highest_PnL_record' />
      </p>
      <div
        className={css({
          pos: 'relative',
          mt: 8,
          bg: 'linear-gradient(0deg, #1c1c1e, #1c1c1e), linear-gradient(163.19deg, #3d02e8 4.35%, #cb00ff 65.48%)',
          bgClip: 'padding-box, border-box',
          bgOrigin: 'border-box',
          border: '1px solid transparent',
          borderRadius: 8,
          tabletDown: {
            _before: {
              content: '""',
              pos: 'absolute',
              inset: '-20px -16px',
              zIndex: -1,
              bg: 'linear-gradient(93.9deg, #3d02e8 9.76%, #3d02e8 51.29%, #cb00ff 101.42%)',
              filter: 'blur(16px)',
              opacity: 0.3,
            },
          },
        })}
      >
        <div className={css({ pos: 'relative', overflow: 'hidden', p: 16 })}>
          <section className={flex({ gap: 8, align: 'center' })}>
            <SymbolDisplay
              symbol={data.symbol}
              icon={<SymbolDisplay.Icon size={24} />}
              mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
            />
            <div className={flex({ gap: 4 })}>
              <OrderSideBadge orderSide={data.side} />
              <LeverageBadge leverage={data.initLeverage} marginType={data.isCrossMargin ? 'Cross' : 'Isolated'} />
            </div>
          </section>
          <section className={flex({ gap: 4, pt: 8, align: 'center' })}>
            <Formatted.Number
              value={realizedPnl}
              precision={UsdtPrecision}
              toFixed
              withColor
              withPlus
              css={{ textStyle: 'Title/S/Monospace' }}
            />
            <Formatted.Percent
              value={realizedPnlPct}
              precision={UsdtPrecision}
              toFixed
              withColor
              withPlus
              withParentheses
              css={{ textStyle: 'Title/S/Monospace' }}
            />
            <ShareIcon
              className={css({ color: 'content.default.level-3', cursor: 'pointer' })}
              onClick={() => {
                const pnlData = {
                  positionId: data.positionId,
                  symbol: data.symbol,
                  side: data.side,
                  entryPrice: data.avgOpenPrice,
                  closePrice: data.avgClosePrice,
                  pnl: realizedPnl,
                  pnlPercentage: realizedPnlPct,
                  initLeverage: data.initLeverage,
                };

                webviewHandler.postMessage(
                  { action: 'link', params: { page: 'sharePerformance', data: pnlData } },
                  () =>
                    overlay.open(({ isOpen, onClose, unmount }) => (
                      <PnlShareModal
                        isOpen={isOpen}
                        onClose={onClose}
                        position={{
                          type: 'perpetual',
                          status: 'closed',
                          evaluationPrice: data.avgClosePrice,
                          ...pnlData,
                        }}
                        onExit={unmount}
                      />
                    )),
                );
              }}
            />
          </section>
        </div>
      </div>
    </section>
  );
};

export default HighestPnl;
